import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Icon,
  Metric,
  SectionTabs,
  Table,
  viLabel,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { useViewport } from '@/lib/viewport';
import { useAuth } from '@/lib/auth';
import {
  historicalImportService,
  type ImportPreview,
  type ImportPreviewRow,
} from '@/services/historicalImportService';

type Step = 'upload' | 'preview' | 'commit';

export function HistoryImportPage() {
  const { isMobile, vw } = useViewport();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<{
    inserted: number;
    updated: number;
    skipped: number;
  } | null>(null);

  function reset() {
    setStep('upload');
    setFileName(null);
    setPreview(null);
    setParseError(null);
    setCommitResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function onFileChosen(file: File) {
    setParsing(true);
    setParseError(null);
    try {
      const buf = await file.arrayBuffer();
      const result = await historicalImportService.preview(buf);
      setFileName(file.name);
      setPreview(result);
      setStep('preview');
    } catch (e) {
      setParseError((e as Error).message);
    } finally {
      setParsing(false);
    }
  }

  async function onCommit() {
    if (!preview || !user) return;
    setCommitting(true);
    try {
      const r = await historicalImportService.commit(preview, user.email);
      setCommitResult(r);
      setStep('commit');
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Quản trị', 'Dữ liệu lịch sử']}
        title="Nhập lịch sử đơn hàng"
        subtitle="Wizard 3 bước: tải tệp · xem trước · áp dụng. Hỗ trợ XLSX và CSV."
        actions={
          step !== 'upload' ? (
            <Button variant="outline" icon="refresh" onClick={reset}>
              Bắt đầu lại
            </Button>
          ) : null
        }
      />
      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <div style={{ marginBottom: 14 }}>
          <SectionTabs
            value={step}
            onChange={() => undefined}
            tabs={[
              { id: 'upload', label: 'Tải tệp', idx: 1 },
              { id: 'preview', label: 'Xem trước', idx: 2 },
              { id: 'commit', label: 'Áp dụng', idx: 3 },
            ]}
          />
        </div>

        {step === 'upload' && (
          <UploadStep
            fileInputRef={fileInputRef}
            parsing={parsing}
            parseError={parseError}
            onFileChosen={onFileChosen}
          />
        )}

        {step === 'preview' && preview && (
          <PreviewStep
            vw={vw}
            fileName={fileName ?? ''}
            preview={preview}
            committing={committing}
            onCommit={onCommit}
            onBack={() => setStep('upload')}
          />
        )}

        {step === 'commit' && commitResult && (
          <CommitStep result={commitResult} onDone={() => navigate('/')} onReset={reset} />
        )}
      </div>
    </div>
  );
}

function UploadStep({
  fileInputRef,
  parsing,
  parseError,
  onFileChosen,
}: {
  fileInputRef: React.RefObject<HTMLInputElement>;
  parsing: boolean;
  parseError: string | null;
  onFileChosen: (f: File) => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
      <Card>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files[0];
            if (f) onFileChosen(f);
          }}
          style={{
            border:
              '2px dashed ' +
              (dragOver ? 'var(--bx-primary)' : 'var(--bx-border)'),
            borderRadius: 12,
            padding: '40px 24px',
            textAlign: 'center',
            background: dragOver ? '#eef2ff' : 'var(--bx-surface-2)',
            transition: 'background 150ms, border-color 150ms',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              background: '#fff',
              color: 'var(--bx-primary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 14,
              border: '1px solid var(--bx-border-muted)',
            }}
          >
            <Icon name="upload" size={22} />
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--bx-text-heading)',
              marginBottom: 4,
            }}
          >
            Kéo thả file XLSX / CSV vào đây
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--bx-text-muted)',
              marginBottom: 18,
              lineHeight: 1.55,
            }}
          >
            Hoặc dùng nút bên dưới để chọn tệp. Tối đa 5MB. Header: Mã kho · Mã khách hàng · Ngày · Số lượng.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFileChosen(f);
            }}
          />
          <Button
            variant="primary"
            icon="upload"
            disabled={parsing}
            onClick={() => fileInputRef.current?.click()}
          >
            {parsing ? 'Đang đọc tệp…' : 'Chọn tệp'}
          </Button>
          {parseError && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: 'var(--bx-error-bg)',
                color: 'var(--bx-error)',
                borderRadius: 8,
                fontSize: 12.5,
                border: '1px solid #fecaca',
              }}
            >
              <Icon name="warning" size={12} style={{ marginRight: 6 }} />
              {parseError}
            </div>
          )}
        </div>
      </Card>

      <Card title="Mẫu file" subtitle="Tải mẫu để chuẩn hóa định dạng">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Button variant="outline" icon="excel" size="sm">
            historical-template.xlsx
          </Button>
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--bx-text-muted)',
              lineHeight: 1.6,
            }}
          >
            <strong>Cột bắt buộc:</strong>
            <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
              <li>Mã kho · ví dụ VN-HN-01</li>
              <li>Mã khách hàng · ví dụ LOREAL-VN</li>
              <li>Ngày · YYYY-MM-DD hoặc DD/MM/YYYY</li>
              <li>Số lượng · số nguyên ≥ 0</li>
            </ul>
            <strong>Cột tùy chọn:</strong> Kênh, Nhóm sản phẩm.
          </div>
        </div>
      </Card>
    </div>
  );
}

function PreviewStep({
  vw,
  fileName,
  preview,
  committing,
  onCommit,
  onBack,
}: {
  vw: number;
  fileName: string;
  preview: ImportPreview;
  committing: boolean;
  onCommit: () => void;
  onBack: () => void;
}) {
  const cols: Column<ImportPreviewRow>[] = [
    {
      header: '#',
      width: 50,
      align: 'right',
      mono: true,
      render: (r) => r.row,
    },
    {
      header: 'Trạng thái',
      render: (r) => (
        <Badge kind={r.status === 'error' ? 'CRITICAL' : (r.status as string)}>
          {viLabel(r.status === 'error' ? 'CRITICAL' : r.status)}
        </Badge>
      ),
    },
    {
      header: 'Kho',
      mono: true,
      render: (r) => r.warehouse_code,
    },
    {
      header: 'Khách hàng',
      mono: true,
      render: (r) => r.customer_code,
    },
    {
      header: 'Ngày',
      mono: true,
      render: (r) => r.date,
    },
    {
      header: 'SL',
      align: 'right',
      mono: true,
      width: 90,
      render: (r) => r.qty,
    },
    {
      header: 'SL cũ',
      align: 'right',
      mono: true,
      width: 90,
      priority: 2,
      render: (r) => (r.prior_qty != null ? r.prior_qty : '—'),
    },
    {
      header: 'Lỗi',
      priority: 2,
      render: (r) =>
        r.errors ? (
          <span style={{ fontSize: 11.5, color: 'var(--bx-error)' }}>
            {r.errors.join('; ')}
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'grid',
          gap: 12,
          marginBottom: 16,
          gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        }}
      >
        <Metric
          label="Mới"
          value={preview.counts.new}
          sublabel="rows sẽ insert"
          deltaTone="up"
        />
        <Metric
          label="Sửa"
          value={preview.counts.update}
          sublabel="rows sẽ update"
          deltaTone="up"
        />
        <Metric
          label="Giữ nguyên"
          value={preview.counts.unchanged}
          sublabel="đã trùng"
        />
        <Metric
          label="Lỗi"
          value={preview.counts.error}
          sublabel="bỏ qua khi áp dụng"
          deltaTone={preview.counts.error > 0 ? 'down' : 'flat'}
        />
      </div>

      <Card
        title={`Xem trước · ${fileName}`}
        subtitle={`${preview.summary.totalCount} dòng tổng cộng · sheet "${preview.summary.sheetName}"`}
        padding={0}
        actions={
          <>
            <Button variant="outline" onClick={onBack}>
              ← Quay lại
            </Button>
            <Button
              variant="primary"
              icon="check"
              onClick={onCommit}
              disabled={
                committing ||
                preview.counts.new + preview.counts.update === 0
              }
            >
              {committing
                ? 'Đang áp dụng…'
                : `Áp dụng ${preview.counts.new + preview.counts.update} dòng`}
            </Button>
          </>
        }
      >
        <Table
          columns={cols}
          rows={preview.rows}
          getRowKey={(r) => r.row}
          emptyText="File rỗng"
          stickyHeader
        />
      </Card>
    </>
  );
}

function CommitStep({
  result,
  onDone,
  onReset,
}: {
  result: { inserted: number; updated: number; skipped: number };
  onDone: () => void;
  onReset: () => void;
}) {
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 24 }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#f0fdf4',
            color: '#15803d',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <Icon name="check" size={28} />
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--bx-text-heading)',
            marginBottom: 6,
          }}
        >
          Import thành công
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--bx-text-muted)',
            marginBottom: 20,
            textAlign: 'center',
            maxWidth: 460,
            lineHeight: 1.55,
          }}
        >
          Toàn bộ thay đổi đã được ghi vào <code>historical_orders</code> + audit_log. Engine
          forecast sẽ tự nhận thay đổi ở lần chạy tiếp theo.
        </div>
        <div
          style={{
            display: 'flex',
            gap: 24,
            marginBottom: 24,
            justifyContent: 'center',
          }}
        >
          <Stat label="Thêm mới" value={result.inserted} tone="success" />
          <Stat label="Cập nhật" value={result.updated} tone="info" />
          <Stat label="Bỏ qua" value={result.skipped} tone="muted" />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="outline" onClick={onReset} icon="upload">
            Import tiếp
          </Button>
          <Button variant="primary" onClick={onDone} iconRight="arrowRight">
            Về trang chủ
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'success' | 'info' | 'muted';
}) {
  const color =
    tone === 'success'
      ? '#15803d'
      : tone === 'info'
        ? 'var(--bx-primary)'
        : 'var(--bx-text-muted)';
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--bx-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 700,
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );
}
